import { makeAutoObservable } from 'mobx';
import RootStore from 'stores/RootStore';
import { RouteType } from 'types/RouteType';

interface UserEntity {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  team: string;
  adminUser: boolean;
  roles?: string | null;
}

interface ServiceStoreType {
  entities: UserEntity[];
  patch: (id: number, data: object) => Promise<unknown>;
  find: (query?: object) => Promise<unknown>;
}


interface RolePermissionEntity {
  id: number;
  role: string;
  routePath?: string | null;
  appName: string;
  location?: string | null;
  accessAllowed: boolean;
  createAllowed: boolean;
  findAllowed: boolean;
  patchAllowed: boolean;
  removeAllowed: boolean;
}

interface RolePermissionsStoreType {
  entities: RolePermissionEntity[];
  find: (query?: object) => Promise<unknown>;
  create: (data: object) => Promise<unknown>;
  patch: (id: number, data: object) => Promise<unknown>;
  remove: (id: number) => Promise<unknown>;
}

const AVAILABLE_ROLES = ['QC', 'PM', 'Production', 'Plant Management', 'Admin'] as const;

class UserManagementStore {
  rootStore: RootStore;
  searchFilter: string = '';

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  get userStore(): ServiceStoreType {
    return this.rootStore.userStore as unknown as ServiceStoreType;
  }

  get users(): UserEntity[] {
    if (!this.userStore?.entities) return [];
    return this.userStore.entities.slice();
  }

  get filteredUsers(): UserEntity[] {
    if (!this.searchFilter) return this.users;
    const filter = this.searchFilter.toLowerCase();
    return this.users.filter((user) => {
      const name = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
      const email = (user.email || '').toLowerCase();
      const department = (user.department || '').toLowerCase();
      const team = (user.team || '').toLowerCase();
      return (
        name.includes(filter) ||
        email.includes(filter) ||
        department.includes(filter) ||
        team.includes(filter)
      );
    });
  }

  get availableRoles(): readonly string[] {
    return AVAILABLE_ROLES;
  }

  get departments(): string[] {
    const depts = new Set<string>();
    this.users.forEach((u) => { if (u.department) depts.add(u.department); });
    return Array.from(depts).sort();
  }

  getTeamsForDepartment(departmentName: string): string[] {
    const teams = new Set<string>();
    this.users
      .filter((u) => u.department === departmentName)
      .forEach((u) => { if (u.team) teams.add(u.team); });
    return Array.from(teams).sort();
  }

  async updateUserDepartment(userId: number, department: string) {
    await this.userStore.patch(userId, { department, team: '' });
  }

  async updateUserTeam(userId: number, team: string) {
    await this.userStore.patch(userId, { team });
  }

  setSearchFilter(value: string) {
    this.searchFilter = value;
  }

  getUserRoles(userId: number): string[] {
    const user = this.users.find((u) => u.id === userId);
    if (!user?.roles) return [];
    return user.roles.split(',').map((r) => r.trim()).filter(Boolean);
  }

  async updateUserRoles(userId: number, roles: string[]) {
    const rolesString = roles.length > 0 ? roles.join(',') : null;
    const isAdmin = roles.includes('Admin');

    await this.userStore.patch(userId, {
      roles: rolesString,
      adminUser: isAdmin,
    });
  }

  async toggleUserRole(userId: number, role: string) {
    const currentRoles = this.getUserRoles(userId);
    let newRoles: string[];

    if (currentRoles.includes(role)) {
      newRoles = currentRoles.filter((r) => r !== role);
    } else {
      newRoles = [...currentRoles, role];
    }

    await this.updateUserRoles(userId, newRoles);
  }

  // --- Role Permissions ---

  get rolePermissionsStore(): RolePermissionsStoreType {
    return this.rootStore.rolePermissionsStore as unknown as RolePermissionsStoreType;
  }

  get rolePermissions(): RolePermissionEntity[] {
    if (!this.rolePermissionsStore?.entities) return [];
    return this.rolePermissionsStore.entities.slice();
  }

  get availableRoutes(): RouteType[] {
    const { routerStore } = this.rootStore;
    if (!routerStore?.routesList) return [];

    // Only show these specific routes in the role permissions UI
    const MANAGED_PATHS = new Set([
      '/home',
      '/master-piece-list',
      '/production/dashboard',
      '/production/schedule',
      '/production/production-schedule-upload',
      '/departments',
      '/user-management',
      '/admin/job-management',
      '/admin/panel-management',
    ]);

    return routerStore.routesList.filter((route: RouteType) => {
      if (!route.path) return false;
      return MANAGED_PATHS.has(route.path);
    });
  }

  isRouteAccessAllowed(role: string, routePath: string): boolean {
    const record = this.rolePermissions.find(
      (rp) => rp.role === role && rp.routePath === routePath && !rp.location
    );
    return record?.accessAllowed || false;
  }

  async toggleRouteAccess(role: string, routePath: string) {
    const record = this.rolePermissions.find(
      (rp) => rp.role === role && rp.routePath === routePath && !rp.location
    );

    if (record) {
      await this.rolePermissionsStore.patch(record.id, {
        accessAllowed: !record.accessAllowed,
      });
    } else {
      await this.rolePermissionsStore.create({
        role,
        routePath,
        appName: this.rootStore.appName,
        accessAllowed: true,
        createAllowed: false,
        findAllowed: false,
        patchAllowed: false,
        removeAllowed: false,
      });
    }
  }

  isCrudAllowed(role: string, routePath: string, flag: 'createAllowed' | 'findAllowed' | 'patchAllowed' | 'removeAllowed'): boolean {
    const record = this.rolePermissions.find(
      (rp) => rp.role === role && rp.routePath === routePath
    );
    return record?.[flag] || false;
  }

  async toggleCrud(role: string, routePath: string, flag: 'createAllowed' | 'findAllowed' | 'patchAllowed' | 'removeAllowed') {
    const record = this.rolePermissions.find(
      (rp) => rp.role === role && rp.routePath === routePath
    );

    if (record) {
      await this.rolePermissionsStore.patch(record.id, {
        [flag]: !record[flag],
      });
    } else {
      await this.rolePermissionsStore.create({
        role,
        routePath,
        appName: this.rootStore.appName,
        accessAllowed: false,
        createAllowed: false,
        findAllowed: false,
        patchAllowed: false,
        removeAllowed: false,
        [flag]: true,
      });
    }
  }
}

export default UserManagementStore;
